import EventEmitter from 'eventemitter3';
import Environment from './Environment';
import { MathHelper } from './MathHelper';

export enum TickerEvent {
    TICK = 'tick',
    SECONDS_INTERVAL = 'SECONDS_INTERVAL',
}
export default class Ticker extends EventEmitter {
    private stats: Stats;

    private _fpsLimit: number = Infinity;
    private frameInterval: number = 0;

    private frame: any;

    private started: number | null = null;
    private previousTime: number;
    private delta: number;

    public start(): void {
        this.stop();

        const now = window.performance.now();
        this.previousTime = now;
        this.started = now;

        if (process?.env.NODE_ENV === Environment.DEVELOPMENT && !this.stats) {
            const Stats = require('stats.js');
            this.stats = new Stats();
            this.stats.showPanel(0);
            this.stats.dom.style.cssText = 'position:absolute;top:0;left:0;cursor:pointer;opacity:0.8;z-index:10000';
            document.body.appendChild(this.stats.dom);
        }

        this.frame = requestAnimationFrame(this.tickIfNeeded);

        this.setInterval(() => {
            this.emit(TickerEvent.SECONDS_INTERVAL);
        }, 1000);
    }

    public stop(): void {
        if (this.started === null) return;

        this.started = null;
        cancelAnimationFrame(this.frame);
    }

    private readonly tickIfNeeded = (now: number): void => {
        this.frame = requestAnimationFrame(this.tickIfNeeded);

        const alignedNow = MathHelper.floor(now, this.frameInterval, this.started);
        const alignedDelta = alignedNow - this.previousTime;
        if (alignedDelta > this.frameInterval || MathHelper.equals(alignedDelta, this.frameInterval)) {
            // This might be multiples of `this.frameInterval`
            this.delta = alignedDelta;

            this.previousTime = alignedNow;

            this.stats?.begin();
            this.emit(TickerEvent.TICK);
            this.stats?.end();
        }
    };

    public get fpsLimit(): number {
        return this._fpsLimit;
    }

    public set fpsLimit(fpsLimit: number) {
        this._fpsLimit = fpsLimit;
        this.frameInterval = 1000 / fpsLimit;
    }

    public setImmediate(resolve: () => void) {
        const onTick = () => {
            this.off(TickerEvent.TICK, onTick);
            resolve();
        };

        this.on(TickerEvent.TICK, onTick);
    }

    public setInterval(resolve: () => void, intervalMs: number): () => void {
        let t = 0;
        const onTick = () => {
            t += this.getDeltaTime();
            if (t >= intervalMs) {
                // This also means we're possibly skipping steps
                t = t % intervalMs;
                resolve();
            }
        };

        this.on(TickerEvent.TICK, onTick);

        return onTick;
    }

    public clearInterval(interval: () => void) {
        if (!interval) return;
        this.off(TickerEvent.TICK, interval);
    }

    public setTimeout(resolve: () => void, time: number): () => void {
        if (time <= 0) resolve();
        let t = 0;
        const onTick = () => {
            t += this.getDeltaTime();
            if (t >= time) {
                this.off(TickerEvent.TICK, onTick);
                resolve();
            }
        };

        this.on(TickerEvent.TICK, onTick);

        return onTick;
    }

    public clearTimeout(interval: () => void) {
        if (!interval) return;
        this.off(TickerEvent.TICK, interval);
    }

    public setTimeoutPromise(time: number) {
        return new Promise(resolve => {
            this.setTimeout(resolve, time);
        });
    }

    public getNow(): number {
        return this.previousTime;
    }

    public getDeltaTime(): number {
        return this.delta;
    }
}
